// @flow
import React from 'react';
import styled from 'styled-components';
import Button, {
  type ButtonProps,
} from '@grnhse/seedling/lib/azalea/components/button';
import { colors } from '@grnhse/seedling/lib/azalea/constants';
import ReactS3Uploader from 'react-s3-uploader';
import uuid from 'uuid';
import { Line } from 'rc-progress';
import EditableRow from 'shared/components/drag-drop/EditableRow';
import { clickOnKeyUp } from 'shared/utils/button';
import { showResponseFlashMessages } from 'shared/utils/request';
import { FileIcon, PaperclipIcon } from '@grnhse/seedling/lib/azalea/icons';
import { clearFix } from 'polished';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('uploads');

const MIN_UPLOAD_DURATION = 1000;

// Helpers
const noop = () => {};
const getErrorResponse = (message) => ({
  response: { status: 400, data: { message } },
});
const updateFile = (uploads, file, update) =>
  uploads.map((upload) =>
    upload.file.name === file.name ? { ...upload, ...update } : upload
  );

type FileWithStart = File & {
  startTime: number,
};

type Upload = {
  file: FileWithStart,
  key: string,
  progress?: number,
  inProgress?: boolean,
};

type Props = {
  children?: typeof React.Children,
  id: string,
  maxSize: number,
  onChange: (any) => any,
  onFileRejected: (File, string) => any,
  onFileFinished: (Upload) => any,
  onFileRemoved: (Upload) => any,
  acceptedMimeTypes: Array<string>,
  acceptedExtensions: Array<string>,
  showFlashErrors: boolean,
  multiple: boolean,
  autoUpload: boolean,
  signingUrl?: string,
  disabled: boolean,
};

export const RejectionReason = {
  MAX_SIZE_EXCEEDED: 'errors.too_large',
  TYPE_NOT_ALLOWED: 'errors.invalid_file_type',
  FILE_NAME_CONFLICT: 'errors.file_name_conflict',
};

export default class S3Uploader extends React.Component<Props, any> {
  static defaultProps = {
    id: uuid.v4(),
    maxSize: 1000 * 1000 * 250, // 250mb
    onChange: noop,
    onFileRejected: noop,
    onFileFinished: noop,
    onFileRemoved: noop,
    acceptedMimeTypes: [],
    acceptedExtensions: [],
    showFlashErrors: true,
    multiple: false,
    autoUpload: true,
    disabled: false,
  };

  state = {
    uploads: [],
    removedUploads: [],
  };

  uploadInput: { value: any } = { value: null };

  formatFileSize = (sizeInBytes: number) =>
    `${Math.trunc(sizeInBytes / 1000 / 1000)}MB`;

  preprocess = (file: File, next: Function) => {
    const { maxSize, acceptedMimeTypes, acceptedExtensions, onFileRejected } =
      this.props;
    let rejection = null;

    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
    const isMimeTypeAccepted =
      acceptedMimeTypes.length === 0 || acceptedMimeTypes.includes(file.type);
    const isExtensionAccepted =
      acceptedExtensions.length === 0 ||
      acceptedExtensions.includes(fileExtension);

    if (file.size > maxSize) {
      rejection = {
        reason: RejectionReason.MAX_SIZE_EXCEEDED,
        options: {
          file_name: file.name,
          max_size: this.formatFileSize(maxSize),
        },
      };
    } else if (!isMimeTypeAccepted || !isExtensionAccepted) {
      rejection = {
        reason: RejectionReason.TYPE_NOT_ALLOWED,
        options: { extension: fileExtension },
      };
    } else if (this.state.uploads.some((u) => u.file.name === file.name)) {
      rejection = {
        reason: RejectionReason.FILE_NAME_CONFLICT,
        options: null,
      };
    }

    if (rejection) {
      this.props.showFlashErrors &&
        showResponseFlashMessages(
          getErrorResponse(t(rejection.reason, rejection.options || {}))
        );
      onFileRejected(file, rejection.reason);
      this.clearInput();
      return;
    }

    const matchingRemovedUpload = this.state.removedUploads.find(
      (upload) => upload.file.name === file.name
    );

    if (matchingRemovedUpload) {
      this.setState(({ uploads, removedUploads }) => ({
        uploads: [...uploads, matchingRemovedUpload],
        removedUploads: removedUploads.filter(
          (ru) => ru.key !== matchingRemovedUpload.key
        ),
      }));
    } else {
      next(file);

      this.setState(({ uploads }) => ({
        uploads: [...uploads, { file, inProgress: true, progress: 0 }],
      }));
    }
  };

  onProgress = (percent: number, status: string, file: FileWithStart) => {
    file.startTime = file.startTime || Date.now();
    this.setState(({ uploads }) => ({
      uploads: updateFile(uploads, file, { progress: percent }),
    }));
  };

  onFinish = (s3Object: { key: string }, file: FileWithStart) => {
    const uploadDuration = Date.now() - file.startTime;
    const timeRemaining = Math.max(MIN_UPLOAD_DURATION - uploadDuration, 0);
    const newUpload = { file, key: s3Object.key };

    setTimeout(() => {
      this.setState(
        ({ uploads }) => ({
          uploads: uploads.map((upload) =>
            upload.file.name === file.name ? newUpload : upload
          ),
        }),
        () => {
          this.props.onFileFinished(newUpload);
          this.clearInput();
        }
      );
    }, timeRemaining);
  };

  onError = (error: string, file: File) => {
    this.setState(({ uploads }) => ({
      uploads: uploads.filter((u) => u.file.name !== file.name),
    }));
    this.clearInput();
    this.props.showFlashErrors &&
      showResponseFlashMessages(
        getErrorResponse(`${file.name} failed to upload: ${error}`)
      );
  };

  onRemoveFile = (upload: Upload) => {
    this.setState(
      ({ uploads, removedUploads }) => ({
        uploads: uploads.filter((u) => u.key !== upload.key),
        removedUploads: [...removedUploads, upload],
      }),
      this.props.onFileRemoved(upload)
    );
  };

  // This needs to happen to allow the input to receive the same file
  // multiple times: https://github.com/odysseyscience/react-s3-uploader/issues/168
  clearInput = () => {
    this.uploadInput.value = null;
  };

  render() {
    const anyInProgress = this.state.uploads.some(
      (upload) => upload.inProgress
    );
    const maxFilesReached =
      !this.props.multiple && this.state.uploads.length > 0;
    const showAttachButton = !anyInProgress && !maxFilesReached;

    return (
      <Container>
        <ul>
          {this.state.uploads.map((upload) =>
            upload.inProgress ? (
              <Uploading key={upload.file.name}>
                <UploadingText>{t('uploading')}</UploadingText>
                <Line
                  percent={upload.progress}
                  trailWidth="3"
                  strokeWidth="3"
                  strokeColor={colors.mediumBlue}
                />
              </Uploading>
            ) : (
              <li key={upload.file.name}>
                <StyledEditableRow
                  item={upload}
                  icon={<StyledFileIcon />}
                  maxLength={175}
                  draggable={false}
                  onRemoveRowClick={this.onRemoveFile}
                  removable
                >
                  {upload.file.name}
                </StyledEditableRow>
              </li>
            )
          )}
        </ul>

        {showAttachButton && (
          <ProxyFileInput
            htmlFor={this.props.id}
            tabIndex="0"
            onKeyUp={clickOnKeyUp}
          >
            <UploadButton block>
              {this.props.children || (
                <span>
                  <StyledPaperclipWrapper>
                    <PaperclipIcon />
                  </StyledPaperclipWrapper>
                  <ButtonText>
                    Attach File
                    {this.props.multiple ? 's' : ''}
                  </ButtonText>
                </span>
              )}
            </UploadButton>
          </ProxyFileInput>
        )}

        <SneakyInput
          id={this.props.id}
          multiple={this.props.multiple}
          accept={this.props.acceptedMimeTypes.join()}
          signingUrl={this.props.signingUrl}
          preprocess={this.preprocess}
          onProgress={this.onProgress}
          onError={this.onError}
          onFinish={this.onFinish}
          onChange={this.props.onChange}
          uploadRequestHeaders={{}}
          inputRef={(cmp) => (this.uploadInput = cmp)}
          autoUpload={this.props.autoUpload}
          disabled={this.props.disabled}
        />
      </Container>
    );
  }
}

const StyledEditableRow = styled(EditableRow)`
  p {
    font-weight: 400;
  }
  border-radius: 3px;
`;

const StyledFileIcon = styled(FileIcon)`
  width: 20px;
  height: 20px;
  margin-right: 10px;
`;

const StyledPaperclipWrapper = styled.span`
  margin-right: 10px;
  float: left;
  width: 20px;
  width: 20px;
`;

const ButtonText = styled.span`
  float: left;
  padding: 2px 0;
`;

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.104.0. To view the error, delete this comment and run Flow. */
const Container = styled.div`
  width: 100%;
`;

const Uploading = styled.div`
  width: 224px;
`;

const UploadingText = styled.div`
  color: ${colors.mediumGrey};
  font-size: 12px;
  letter-spacing: 0.2px;
  line-height: normal;
`;

// this will be the label that acts as a proxy for the real file input
// an approximation of most browsers outline when
// focused via tabbing
const blueOutline = '#5E9ED6';

/**
 * Real HTML file inputs cannot be styled well.
 *
 * To get around this, A label is created that sends a click event to the
 * real HTML file input (one that is hidden from view) so that it behaves
 * like a regular file input.
 * */
const ProxyFileInput = styled.label`
  display: inline-block;
  margin: 0 auto;
  cursor: pointer;

  &:focus {
    outline: 1px dotted ${blueOutline};
    outline: 5px auto -webkit-focus-ring-color;
  }
`;

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.104.0. To view the error, delete this comment and run Flow. */
export const SneakyInput = styled(ReactS3Uploader)`
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
`;

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.104.0. To view the error, delete this comment and run Flow. */
export const UploadButton = styled<ButtonProps>(Button)`
  pointer-events: none;
  overflow-wrap: break-word;
  padding: 8px 12px 8px 10px;
  width: unset;
  ${clearFix()} &:hover {
    color: unset;
  }
`;
